import { MyLoader } from "@/components/loaders/SkeletonLoader";
import { useAuth } from "@/context/AuthContext";
import { formatDate } from "@/helpers/dateHelpers";
import { ThemeAvatar } from "@/MY_COMPONENTS/MY_THEMES/ProfileTheme/ThemeAvatar";
import { ThemeBadge } from "@/MY_COMPONENTS/MY_THEMES/ProfileTheme/ThemeBadge";
import { ThemeCard } from "@/MY_COMPONENTS/MY_THEMES/ProfileTheme/ThemeCard";
import { ThemeContainer } from "@/MY_COMPONENTS/MY_THEMES/ProfileTheme/ThemeContainer";
import { ThemeDetailRow } from "@/MY_COMPONENTS/MY_THEMES/ProfileTheme/ThemeDetailRow";
import { ThemeGrid } from "@/MY_COMPONENTS/MY_THEMES/ProfileTheme/ThemeGrid";
import { ThemeSection } from "@/MY_COMPONENTS/MY_THEMES/ProfileTheme/ThemeSection";
import { ThemeStatusIndicator } from "@/MY_COMPONENTS/MY_THEMES/ProfileTheme/ThemeStatusIndicator";
import { MdAccessTime, MdCalendarToday, MdCheckCircle, MdEmail, MdOutlineNotificationsActive, MdOutlineSecurity, MdPhone, MdSupervisorAccount, MdVerified } from "react-icons/md";


// Example usage of the refactored MyProfile using theme components
export const MyProfile: React.FC = () => {
  const { user } = useAuth();

  if (!user) return <MyLoader />;

  return (
    <ThemeContainer maxWidth="md">
      {/* Profile Header */}
      <ThemeCard>
        <div className="flex flex-col md:flex-row items-center md:items-start gap-6">
          <ThemeAvatar
            src={user.profile_picture_url}
            alt={user.display_name || user.full_name}
            size="lg"
            verified={user.email_verified}
            name={user.full_name}
          />

          <div className="flex-1 text-center md:text-left space-y-2">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
              <h1 className="text-3xl font-semibold text-gray-900 dark:text-gray-100">
                {user.full_name}
              </h1>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                User ID: {user.user_id}
              </div>
            </div>

            {/* Roles Badges */}
            <div className="flex flex-wrap gap-2 justify-center md:justify-start">
              {user.role_names?.map((role, index) => (
                <ThemeBadge key={index} variant="primary" icon={<MdSupervisorAccount />}>
                  {role}
                </ThemeBadge>
              ))}
              {user.is_superadmin && (
                <ThemeBadge variant="purple" icon={<MdOutlineSecurity />}>
                  SUPER ADMIN
                </ThemeBadge>
              )}
            </div>

            {/* Contact Info */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:gap-4 text-gray-600 dark:text-gray-300 text-sm">
              <div className="flex items-center gap-1">
                <MdEmail className="text-gray-400" />
                <span>{user.email}</span>
                {user.email_verified && (
                  <MdCheckCircle className="text-green-500 ml-1" title="Email Verified" />
                )}
              </div>
              {user.phone && (
                <div className="flex items-center gap-1">
                  <MdPhone className="text-gray-400" />
                  <span>{user.phone}</span>
                  {user.phone_verified && (
                    <MdCheckCircle className="text-green-500 ml-1" title="Phone Verified" />
                  )}
                </div>
              )}
            </div>

            {/* Account Status */}
            <div className="flex items-center gap-2">
              <ThemeStatusIndicator status={user.status === 'active' ? 'active' : 'pending'} />
              {user.two_factor_enabled && (
                <ThemeBadge variant="info" icon={<MdOutlineSecurity />}>
                  2FA Enabled
                </ThemeBadge>
              )}
            </div>
          </div>
        </div>
      </ThemeCard>

      {/* Account Details Grid */}
      <ThemeGrid columns={2} >
        <ThemeSection gap="md" title="Account Information" icon={<MdCalendarToday />}>
          <ThemeDetailRow
            icon={<MdCalendarToday />}
            label="Member Since"
            value={formatDate(user.created_at as any)}
          />
          <ThemeDetailRow
            icon={<MdAccessTime />}
            label="Last Login"
            value={formatDate(user.last_login_at as any)}
          />
          <ThemeDetailRow
            icon={<MdEmail />}
            label="Email Verified"
            value={user.email_verified ? formatDate(user.email_verified_at as any) : 'Not verified'}
          />
          <ThemeDetailRow
            icon={<MdVerified />}
            label="Account Status"
            value={user.status}
          />
        </ThemeSection>

        <ThemeSection title="Security Settings" icon={<MdOutlineSecurity />}>
          <div className="flex justify-between items-center">
            <span className="text-gray-600 dark:text-gray-300">Two-Factor Authentication</span>
            <ThemeStatusIndicator 
              status={user.two_factor_enabled ? 'enabled' : 'disabled'}
              size="sm"
            />
          </div>
          <div className="flex justify-between items-center">
            <span className="text-gray-600 dark:text-gray-300">Phone Verified</span>
            <ThemeStatusIndicator 
              status={user.phone_verified ? 'verified' : 'inactive'}
              size="sm"
            />
          </div>
        </ThemeSection>
      </ThemeGrid>

      {/* Permissions Summary */}
      {user.permissions && (
        <ThemeSection 
          title="Permissions" 
          icon={<MdOutlineNotificationsActive />}
        >
          <ThemeGrid columns={4} gap="sm">
            {Object.entries(user.permissions).map(([key, value]) => (
              <div key={key} className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${value ? 'bg-green-500' : 'bg-gray-300'}`} />
                <span className="text-sm text-gray-600 dark:text-gray-300 capitalize">
                  {key.replace(/_/g, ' ')}
                </span>
              </div>
            ))}
          </ThemeGrid>
        </ThemeSection>
      )}
    </ThemeContainer>
  );
};